import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaUsers, FaUserCheck, FaClock, FaUserPlus, FaCrown, FaTrash, FaSearch, FaUser, FaEnvelope } from 'react-icons/fa';
import { getMembers, inviteMemberByEmail, removeMember, searchUsers } from '../api/client';
import ConfirmDialog from './ConfirmDialog';

const TripMembersList = ({ requestId, isOwner = false, hasInviteAccess = false }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Invite states
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [email, setEmail] = useState('');
  const [includePhotos, setIncludePhotos] = useState(true);
  const [adding, setAdding] = useState(false);

  // Typeahead search states
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, memberId: null });

  const loadMembers = useCallback(async () => {
    if (!requestId) return;
    try {
      setLoading(true);
      const data = await getMembers(requestId);
      setMembers(data.members || []);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      setShowDropdown(false);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await searchUsers(query);
      const memberEmails = members.map(m => m.email?.toLowerCase());
      const filteredResults = (response.results || []).filter(
        user => !memberEmails.includes(user.email?.toLowerCase())
      );
      setSearchResults(filteredResults);
      setShowDropdown(filteredResults.length > 0);
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [members]);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (value.length >= 1) {
      setSearchLoading(true);
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 50);
  };

  const handleSelectUser = (user) => {
    setEmail(user.email);
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleInviteByEmail = async () => {
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setAdding(true);
      setError('');
      const result = await inviteMemberByEmail(requestId, email, includePhotos);
      if (result.status === 'active') {
        setSuccess('Member added successfully!');
      } else {
        setSuccess('Invitation sent successfully!');
      }
      setEmail('');
      setShowInviteForm(false);
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to invite member');
      setTimeout(() => setError(''), 3000);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = (memberId) => {
    setConfirmDialog({ isOpen: true, memberId });
  };

  const confirmRemoveMember = async () => {
    const memberId = confirmDialog.memberId;
    setConfirmDialog({ isOpen: false, memberId: null });

    try {
      setError('');
      await removeMember(requestId, memberId);
      setSuccess('Member removed');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove member');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="mt-6 pt-6 border-t border-neutral-300">
        <h3 className="text-sm font-semibold text-neutral-700 mb-4 flex items-center gap-2">
          <FaUsers className="text-teal-500" /> Trip Members
        </h3>
        <div className="text-center py-4 text-neutral-500">Loading members...</div>
      </div>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-neutral-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
          <FaUsers className="text-teal-500" /> Trip Members ({members.length})
        </h3>
        {isOwner && hasInviteAccess && !showInviteForm && (
          <button
            onClick={() => setShowInviteForm(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 rounded-lg shadow-md hover:shadow-lg transition-all"
          >
            <FaUserPlus />
            Invite Member
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Invite Form */}
      {isOwner && hasInviteAccess && showInviteForm && (
        <div className="mb-6 p-4 bg-teal-50 rounded-xl border border-teal-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FaEnvelope className="text-teal-600" />
              <h4 className="font-medium text-neutral-900 text-sm">Invite Member by Email</h4>
            </div>
            <button
              onClick={() => setShowInviteForm(false)}
              className="text-neutral-400 hover:text-neutral-600 text-sm"
            >
              Cancel
            </button>
          </div>

          <div className="flex gap-2 mb-3">
            <div className="flex-1 relative">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm" />
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onFocus={() => email.length >= 2 && searchResults.length > 0 && setShowDropdown(true)}
                  placeholder="Search or enter email..."
                  className="w-full pl-9 pr-3 py-2 border-2 border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-100 outline-none focus:border-teal-400 text-sm"
                  autoComplete="off"
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Typeahead Dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-1 bg-white border-2 border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                >
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleSelectUser(user)}
                      className="w-full px-3 py-2 flex items-center gap-2 hover:bg-teal-50 transition-colors text-left border-b border-neutral-100 last:border-b-0"
                    >
                      <div className="w-7 h-7 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <FaUser className="text-white text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-neutral-900 truncate text-sm">
                          {user.full_name || 'User'}
                        </p>
                        <p className="text-xs text-neutral-500 truncate">
                          {user.email}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleInviteByEmail}
              disabled={!email || adding}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 text-sm font-medium"
            >
              {adding ? '...' : 'Invite'}
            </button>
          </div>

          {/* Include Photos Toggle */}
          <label className="flex items-center gap-2 text-xs text-neutral-600">
            <input
              type="checkbox"
              checked={includePhotos}
              onChange={(e) => setIncludePhotos(e.target.checked)}
              className="rounded border-neutral-300 text-teal-500 focus:ring-teal-500"
            />
            Allow member to view photos
          </label>
        </div>
      )}

      {/* Members List */}
      {members.length === 0 ? (
        <div className="text-center py-8 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
          <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full flex items-center justify-center">
            <FaUsers className="text-2xl text-white" />
          </div>
          <p className="text-neutral-600 font-medium mb-1">No members yet</p>
          <p className="text-neutral-400 text-sm mb-4">Invite friends and family to collaborate on this trip</p>
          {isOwner && hasInviteAccess && !showInviteForm && (
            <button
              onClick={() => setShowInviteForm(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <FaUserPlus />
              Invite Your First Member
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-3 p-3 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg border border-teal-100"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                member.role === 'owner'
                  ? 'bg-gradient-to-br from-amber-400 to-amber-500'
                  : member.status === 'active'
                    ? 'bg-gradient-to-br from-teal-400 to-teal-500'
                    : 'bg-gradient-to-br from-yellow-400 to-yellow-500'
              }`}>
                {member.role === 'owner' ? (
                  <FaCrown className="text-white text-sm" />
                ) : member.status === 'active' ? (
                  <FaUserCheck className="text-white text-sm" />
                ) : (
                  <FaClock className="text-white text-sm" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-neutral-800 truncate text-sm">
                  {member.full_name || member.email?.split('@')[0] || 'Member'}
                </p>
                <p className="text-xs text-neutral-500 truncate">
                  {member.role === 'owner' ? (
                    <span className="text-amber-600 font-medium">Owner</span>
                  ) : member.status === 'active' ? (
                    <span className="text-teal-600">Active</span>
                  ) : (
                    <span className="text-yellow-600">Pending</span>
                  )}
                </p>
              </div>
              {isOwner && member.role !== 'owner' && (
                <button
                  onClick={() => handleRemoveMember(member.id)}
                  className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove member"
                >
                  <FaTrash className="text-xs" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, memberId: null })}
        onConfirm={confirmRemoveMember}
        title="Remove Member"
        message="Are you sure you want to remove this member from the trip? They will no longer have access to the itinerary."
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default TripMembersList;
